import {
    AfterViewInit,
    Component,
    ElementRef,
    inject,
    input,
    ViewChild,
} from '@angular/core';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';
import { DialogHeader } from '../dialog/header/dialog-header.component';
import { DialogContent } from '../dialog/content/dialog-content.component';
import { DialogActions } from '../dialog/actions/dialog-actions.component';
import { DialogInfo, DialogManager } from '../dialog/dialog.service';

/** Resultado retornado ao fechar um diálogo de confirmação. */
export enum ConfirmDialogStatus {
    /** O usuário cancelou (ou fechou) o diálogo. */
    Cancel = 'cancel',
    /** O usuário confirmou a ação. */
    Confirm = 'confirm',
}

/**
 * Diálogo de confirmação padrão (título, mensagem e botões cancelar/confirmar).
 *
 * Normalmente aberto via {@link DialogManager}. Ao fechar, retorna um
 * {@link ConfirmDialogStatus} indicando a escolha do usuário. Suporta ícones
 * opcionais nos botões, variante de cor no confirmar (ex.: `danger` para
 * exclusões) e foco inicial no botão de cancelar.
 */
@Component({
    selector: 'confirm-dialog',
    standalone: true,
    templateUrl: './confirm-dialog.component.html',
    imports: [Button, Icon, DialogHeader, DialogContent, DialogActions],
})
export class ConfirmDialog implements AfterViewInit {
    /** Mensagem exibida no corpo do diálogo. */
    protected message = input<string>('Confirma a ação?');
    /** Título do diálogo. */
    protected title = input<string>('Confirmação');
    /** Rótulo do botão de cancelar. */
    protected cancelButtonLabel = input<string>('Cancelar');
    /** Rótulo do botão de confirmar. */
    protected confirmButtonLabel = input<string>('Confirmar');
    /**
     * Variante de cor do botão de confirmar. Use `'danger'` (vermelho) em
     * confirmações destrutivas, como remoções/exclusões.
     */
    protected confirmButtonVariant = input<'default' | 'primary' | 'danger'>(
        'default',
    );
    /**
     * Variante de cor do botão de cancelar. Padrão `'default'` (azul claro);
     * use `'danger'` (vermelho) quando o cancelar for a ação enfatizada.
     */
    protected cancelButtonVariant = input<'default' | 'primary' | 'danger'>(
        'default',
    );
    /** Ícone (Font Awesome) opcional do botão de cancelar. */
    protected cancelButtonIcon = input<string | null>(null);
    /** Ícone (Font Awesome) opcional do botão de confirmar. */
    protected confirmButtonIcon = input<string | null>(null);
    /**
     * Quando `true`, o botão de cancelar recebe o foco ao abrir o diálogo —
     * útil em confirmações destrutivas, para que a ação padrão seja a segura
     * (desistir), evitando exclusões acidentais por Enter.
     */
    protected focusCancelOnOpen = input<boolean>(false);

    protected dialogInfo = inject(DialogInfo);
    private dialogManager = inject(DialogManager);

    /** Referência ao botão de cancelar, para receber o foco inicial. */
    @ViewChild('cancelButton', { read: ElementRef })
    private readonly cancelButtonRef?: ElementRef<HTMLElement>;

    public ngAfterViewInit(): void {
        if (!this.focusCancelOnOpen()) {
            return;
        }
        // Defer para depois da montagem/animação do diálogo, garantindo que o
        // elemento já esteja no DOM e focável.
        setTimeout(() => {
            this.cancelButtonRef?.nativeElement
                .querySelector('button')
                ?.focus();
        });
    }

    protected onCancelClick(): void {
        this.dialogManager.close(
            this.dialogInfo.id!,
            ConfirmDialogStatus.Cancel,
        );
    }

    protected onConfirmClick(): void {
        this.dialogManager.close(
            this.dialogInfo.id!,
            ConfirmDialogStatus.Confirm,
        );
    }

    protected onCloseDialog(): void {
        this.dialogManager.close(
            this.dialogInfo.id!,
            ConfirmDialogStatus.Cancel,
        );
    }
}
